import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { ANIMATION_SPEED } from '../../constants/config';
import { easeOutCubic } from '../../utils/animation/easings';
import {
  calculateSpiralPosition,
  updateItemPosition,
} from '../../utils/geometry/spiral';
import type { Project, ResponsiveValues } from '../../types';
import React, { useEffect, useRef } from 'react';
import { useAppStore } from '../../store/useAppStore';

interface PositionAnimationProps {
  groupRef: React.RefObject<THREE.Group>;
  allImagesLoaded: boolean;
  isInitialAnimationComplete: React.MutableRefObject<boolean>;
  setInitAnimation: (value: number) => void;
  initAnimation: number;
  scrollRef: React.RefObject<number>;
  selectedIndex: number | null;
  projects: Project[];
  values: ResponsiveValues;
  setTransitionProgress: (value: number) => void;
  onNearestProjectChange: (index: number) => void;
}

const calculateNearestProject = (
  groupRef: React.RefObject<THREE.Group>,
  selectedIndex: number | null,
  values: ResponsiveValues,
  scrollRef: React.RefObject<number>
) => {
  // Si un projet est sélectionné, on montre celui-là dans l'indicateur
  if (selectedIndex !== null) {
    return selectedIndex;
  }

  if (!groupRef.current) return 0;

  // Angle entre chaque projet (2π/7 pour 7 projets)
  const anglePerProject = (Math.PI * 2) / values.projectsPerLoop;

  // Normaliser le scroll offset entre 0 et 2π
  const normalizedOffset = ((scrollRef.current ?? 0) * 2) % (Math.PI * 2);

  // Calculer l'index en divisant par l'angle entre chaque projet
  // Commencer par le milieu (0 devient le milieu)
  const middleOffset = Math.floor(values.projectsPerLoop / 2);
  const index = -Math.round(normalizedOffset / anglePerProject) + middleOffset;

  // S'assurer que l'index est dans les limites
  return (index + values.projectsPerLoop) % values.projectsPerLoop;
};

export const usePositionAnimation = ({
  groupRef,
  allImagesLoaded,
  isInitialAnimationComplete,
  setInitAnimation,
  initAnimation,
  scrollRef,
  selectedIndex,
  projects,
  values,
  setTransitionProgress,
  onNearestProjectChange,
}: PositionAnimationProps) => {
  const currentTransitionProgress = React.useRef(0);
  const lastNearestIndex = useRef<number>(0);
  const { camera, viewport } = useThree(); // Utiliser la caméra par défaut de Three.js

  // Utiliser useEffect pour mettre à jour l'état global
  useEffect(() => {
    if (initAnimation === 1 && !isInitialAnimationComplete.current) {
      isInitialAnimationComplete.current = true;
      useAppStore.getState().setInitialAnimationComplete(true);
    }
  }, [initAnimation, isInitialAnimationComplete]);

  useFrame((_, delta) => {
    if (!groupRef.current || !allImagesLoaded) return;

    // Mise à jour globale du temps
    const timeValue = performance.now() * 0.001; // Convertit en secondes

    // Mise à jour du temps pour tous les matériaux
    projects.forEach((_, index) => {
      const child = groupRef.current?.children[index];
      if (!child) return;

      const mesh = child as THREE.Mesh;
      const material = mesh.material as THREE.ShaderMaterial;

      if (material && material.uniforms) {
        material.uniforms.time.value = timeValue;
      }
    });

    // Animation d'initialisation avec easing
    if (!isInitialAnimationComplete.current) {
      setInitAnimation(
        Math.min(initAnimation + delta * ANIMATION_SPEED.INIT, 1)
      );
    }

    // Animation de transition avec easing
    currentTransitionProgress.current = THREE.MathUtils.lerp(
      currentTransitionProgress.current,
      selectedIndex !== null ? 1 : 0,
      delta * ANIMATION_SPEED.TRANSITION
    );
    setTransitionProgress(currentTransitionProgress.current);

    // Mise à jour des positions avec interpolation
    projects.forEach((project, index) => {
      const child = groupRef.current?.children[index];
      if (!child) return;

      const baseAngle = (index / values.projectsPerLoop) * Math.PI * 2;
      const targetIndex = 0.5;
      const targetAngle = (targetIndex / values.projectsPerLoop) * Math.PI * 2;
      const scrollOffset = ((scrollRef.current ?? 0) * 2) % (Math.PI * 2);

      const progress = easeOutCubic(initAnimation);
      const totalAngle = baseAngle + targetAngle;
      const animatedAngle = totalAngle * progress + scrollOffset;

      const spiralRotY = animatedAngle * 2 + Math.PI;
      const spiralScale = values.baseScale + Math.sin(baseAngle * 0.3) * 0.15;

      const [spiralX, spiralY, spiralZ] = calculateSpiralPosition(
        index,
        animatedAngle,
        values
      );

      let targetRotation = spiralRotY;
      if (selectedIndex === index) {
        const tempObject = new THREE.Object3D();
        tempObject.position.set(0, 2, 5);
        tempObject.lookAt(camera.position);
        targetRotation = tempObject.rotation.y + Math.PI;
      }

      updateItemPosition(
        child,
        index,
        selectedIndex,
        {
          spiralX,
          spiralY,
          spiralZ,
          spiralRotY: targetRotation,
          spiralScale,
        },
        currentTransitionProgress.current,
        values,
        viewport,
        project.thumbnail
      );
    });

    // Passer selectedIndex au calcul
    const nearestIndex = calculateNearestProject(
      groupRef,
      selectedIndex,
      values,
      scrollRef
    );
    if (nearestIndex !== lastNearestIndex.current) {
      lastNearestIndex.current = nearestIndex;
      onNearestProjectChange(nearestIndex);
    }
  });
};
